/**
 * Which provider a model ref runs on — the one answer every composer uses to
 * check files before they are sent.
 *
 * The catalogue (`GET /ai/models/`) is the authority: each entry carries its
 * `provider`, and a ref is found by its family key ('flash', 'sonnet'), its
 * pinned release, or any release of the family (`findCatalogEntry`). Family
 * keys say nothing about their provider — 'flash' is Google, 'opus' is
 * Anthropic — so guessing from the spelling is wrong. Staging 2026-10-20: a
 * 6.3 MB photo on a Flash-only agent was refused against Anthropic's 5 MB
 * image limit, while the server checks it against Google's 7 MB and takes it.
 *
 * A ref the catalogue does not hold is read by its release prefix only
 * ('claude-…', 'gpt-…', 'gemini-…', 'kimi-…'); anything else is unknown —
 * null, never a default provider — and the caller lets the server decide.
 *
 * Pure and dependency-free, so `tests/unit/conversations/model_provider.test.mjs`
 * runs the real code.
 */

import { bare, findCatalogEntry, type LabelCatalogEntry } from './modelLabels';

/** The fields of a `GET /ai/models/` entry this module reads. */
export interface ProviderCatalogEntry extends LabelCatalogEntry {
  provider?: string;
}

const RELEASE_PREFIXES: ReadonlyArray<readonly [RegExp, string]> = [
  [/^claude-/, 'anthropic'],
  [/^(gpt-|o\d)/, 'openai'],
  [/^gemini-/, 'google'],
  [/^(kimi-|moonshot-)/, 'moonshot'],
];

/** The provider `ref` runs on, or null when it cannot be told. */
export function providerOfModel(
  models: readonly ProviderCatalogEntry[] | null | undefined,
  ref: string | null | undefined,
): string | null {
  if (!ref) return null;
  const entry = findCatalogEntry(models, ref);
  if (entry?.provider) return entry.provider;
  const r = bare(ref).toLowerCase();
  return RELEASE_PREFIXES.find(([re]) => re.test(r))?.[1] ?? null;
}
